import { logoImages, utilsImages } from '@/assets/images'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PageLoader } from '@/components/ui/loader'
import { endPoint } from '@/constant/end-point'
import useAPI from '@/hooks/use-api'
import { useTheme } from '@/hooks/use-theme'
import { useUnsavedChangesWarning } from '@/hooks/use-unsaved-changes-warning'
import { cn } from '@/lib/utils'
import {
  forgetPasswordSchema,
  type ForgetPasswordSchemaType,
  type ResetPasswordSchemaType,
} from '@/schema/auth/forget-password'
import type { PasswordPolicy } from '@/utils/password-policy'
import { createPasswordSchema, DEFAULT_PASSWORD_POLICY } from '@/utils/password-policy'
import { scrollToTop } from '@/utils/scroll-to-top'
import { revokeOtherServerSessions } from '@/utils/session-actions'
import { useAuth, useSignIn } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

export function ForgotPasswordPage({ className, ...props }: React.ComponentProps<'div'>) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const { isSignedIn } = useAuth()
  const { getOne } = useAPI<PasswordPolicy>()
  const { isLoaded, signIn, setActive } = useSignIn()
  const [resetEmail, setResetEmail] = useState<string>('')
  const [secondFactor, setSecondFactor] = useState(false)
  const [successfulCreation, setSuccessfulCreation] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) return

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [resendCooldown])

  const { data: policyData } = useQuery({
    queryKey: [endPoint.passwordPolicyByEmail, resetEmail],
    queryFn: async () => {
      if (!resetEmail) return { data: DEFAULT_PASSWORD_POLICY }
      const response = await getOne(endPoint.passwordPolicyByEmail, { email: resetEmail })
      if (!response?.status) return { data: DEFAULT_PASSWORD_POLICY }
      return { data: (response.data ?? DEFAULT_PASSWORD_POLICY) as PasswordPolicy }
    },
    enabled: successfulCreation && !!resetEmail,
  })

  const policy = policyData?.data ?? DEFAULT_PASSWORD_POLICY
  const resetPasswordSchema = useMemo(
    () =>
      z
        .object({
          code: z.string().length(6, 'OTP must be 6 digits'),
          password: createPasswordSchema(policy, { email: resetEmail }),
          confirmPassword: createPasswordSchema(policy, { email: resetEmail }),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: 'Passwords do not match',
          path: ['confirmPassword'],
        }),
    [policy, resetEmail]
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ResetPasswordSchemaType | ForgetPasswordSchemaType>({
    resolver: zodResolver(successfulCreation ? resetPasswordSchema : forgetPasswordSchema),
  })

  useUnsavedChangesWarning({ when: isDirty && !isSubmitting })
  useEffect(() => {
    if (isSignedIn) navigate('/', { replace: true })
  }, [isSignedIn, navigate])

  if (!isLoaded) return <PageLoader />

  // Send the password reset code to the user's email
  async function create(email: string) {
    try {
      await signIn?.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })

      setResetEmail(email)
      setSuccessfulCreation(true)
      toast.success('Reset code sent!', {
        description: 'Please check your email for the password reset code',
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset code'
      toast.error('Failed to send reset code', {
        description: errorMessage,
      })
    }
  }

  async function reset(code: string, password: string) {
    try {
      const result = await signIn?.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password,
      })

      // Check if 2FA is required
      if (result?.status === 'needs_second_factor') {
        setSecondFactor(true)
        toast.info('Two-factor authentication required', {
          description: 'Please complete 2FA to continue',
        })
      } else if (result?.status === 'complete') {
        if (result.createdSessionId && setActive) await setActive({ session: result.createdSessionId })

        try {
          await revokeOtherServerSessions()
        } catch {
          // Continue even if other session revocation fails.
        }

        toast.success('Password reset successful!', {
          description: 'Other active sessions have been signed out for your security',
        })
        navigate('/', { replace: true })
      } else {
        console.log('Reset not complete:', result)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password'
      toast.error('Failed to reset password', {
        description: errorMessage,
      })
    }
  }

  const onSubmit = async (data: ForgetPasswordSchemaType | ResetPasswordSchemaType) => {
    if (!successfulCreation) create((data as ForgetPasswordSchemaType).email as string)
    else reset((data as ResetPasswordSchemaType).code as string, (data as ResetPasswordSchemaType).password as string)
  }

  const handleResendResetCode = async () => {
    if (resendCooldown > 0 || !resetEmail) return

    try {
      await signIn?.create({
        strategy: 'reset_password_email_code',
        identifier: resetEmail,
      })
      toast.success('Reset code resent!', {
        description: 'Please check your email for the reset code',
      })
      setResendCooldown(60)
    } catch {
      toast.error('Failed to resend reset code')
    }
  }

  return (
    <div className='flex min-h-screen w-full items-center justify-center bg-background'>
      <div className={cn('flex  flex-col gap-6 xl:w-[60%] lg:w-[80%] md:w-full w-full', className)} {...props}>
        <Card className='overflow-hidden p-0 shadow-md'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <form
              onSubmit={handleSubmit(onSubmit, () => {
                scrollToTop()
              })}
              className='p-6 md:p-8'
            >
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col items-center text-center'>
                  <img
                    loading='lazy'
                    decoding='async'
                    src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                    alt='Image'
                    className='h-12 w-auto'
                  />
                  <h1 className='text-2xl font-medium pt-3'>Forgot Password?</h1>
                  <p className='text-muted-foreground text-sm'>
                    {!successfulCreation
                      ? 'Enter your email to receive a reset code'
                      : 'Enter the code from your email and your new password'}
                  </p>
                </div>
                {!successfulCreation && (
                  <>
                    <div className='grid gap-3'>
                      <Input
                        id='email'
                        type='text'
                        placeholder='Enter your email address'
                        {...register('email')}
                        error={'email' in errors ? (errors.email?.message as string | undefined) : undefined}
                        required
                      />
                    </div>
                    <Button type='submit' className='w-full' disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          <span>Sending reset code...</span>
                        </>
                      ) : (
                        'Send password reset code'
                      )}
                    </Button>
                  </>
                )}
                {successfulCreation && (
                  <>
                    <div className='space-y-3'>
                      <Input
                        id='code'
                        type='text'
                        placeholder='Enter the reset code from your email'
                        {...register('code')}
                        error={'code' in errors ? (errors.code?.message as string | undefined) : undefined}
                        required
                      />
                      <Input
                        id='password'
                        type='password'
                        placeholder='Enter your new password'
                        {...register('password')}
                        error={'password' in errors ? (errors.password?.message as string | undefined) : undefined}
                        required
                      />
                      <Input
                        id='confirm-password'
                        type='password'
                        placeholder='Confirm your new password'
                        {...register('confirmPassword')}
                        error={
                          'confirmPassword' in errors
                            ? (errors.confirmPassword?.message as string | undefined)
                            : undefined
                        }
                        required
                      />
                    </div>

                    <p
                      onClick={resendCooldown > 0 ? undefined : handleResendResetCode}
                      className={cn(
                        'text-xs text-blue-400',
                        resendCooldown > 0
                          ? 'cursor-not-allowed text-muted-foreground'
                          : 'cursor-pointer hover:underline'
                      )}
                    >
                      {resendCooldown > 0 ? `Resend reset code in ${resendCooldown}s` : 'Resend reset code'}
                    </p>
                    <Button type='submit' className='w-full' disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          <span>Resetting password...</span>
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </>
                )}
                {secondFactor && (
                  <div className='text-center text-sm text-muted-foreground'>
                    Two-factor authentication is required, but this UI does not handle that yet.
                  </div>
                )}
                <div className='text-center text-sm'>
                  Remember your password?&nbsp;
                  <Link to='/sign-in' className='underline underline-offset-4'>
                    Sign in
                  </Link>
                </div>
              </div>
            </form>
            <div className='relative hidden md:block'>
              <img
                loading='lazy'
                decoding='async'
                src={utilsImages.loginPageSide}
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
        <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
          By clicking continue, you agree to our <Link to='/terms-of-service'>Terms of Service</Link> and&nbsp;
          <Link to='/privacy-policy'>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  )
}
